import { Button } from 'antd';
import Modal from 'antd/es/modal';
import cn from 'classnames';
import FocusTrap from 'focus-trap-react';
import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import '../../assets/styles/custom/pages/interaction_index.scss';
import Loader from '../../components/Loader';
import { checkIfChatAnswered, watchChat } from '../../helpers/Chat';
import {
    getCommonRoutePath,
    getUniversityId,
    isIframelessVersion,
    isInIframe,
    isUniGroup,
    trackGetStarted,
} from '../../helpers/Helpers';
import { bindDialog } from '../../helpers/pusherHelper';
import { localStorageWrapper, sessionStorageWrapper } from '../../helpers/storageFactory';
import {
    ACCOUNT,
    CLOSE_LOGIN_POPUP,
    CLOSE_REGISTRATION_POPUP,
    MANDATORY_QUESTIONS,
    OPEN_NOTIFICATION_METHOD_POPUP,
    REMOVE_FIRST_MESSAGE,
    SAVE_MANDATORY_QUESTIONS,
    SAVE_UNIVERSITY_QUESTIONS,
    SAVE_UNIVERSITY_USER_UTM_DATA,
    UNIVERSITY_QUESTIONS,
    startRegistration,
} from '../../redux/actions/accountActions';
import { CLOSE_ACTION_MODAL, OPEN_ACTION_MODAL } from '../../redux/actions/componentsActions';
import {
    CHANGE_CHATS_TAB,
    CHAT_INFO,
    DISPLAY_PROSPECT_INFO,
    RESET_ACTIVE_CHAT,
    SET_ACCEPTED,
    SHOW_CHAT_MEMBERS,
} from '../../redux/actions/currentChatActions';
import {
    ADD_NEW_DIALOG_TO_IDS,
    AMBASSADOR_DIALOGS_INFO,
    CLEAR_DIALOGS_STORE,
    GROUP_DIALOGS_INFO,
    PROSPECT_DIALOGS_INFO,
} from '../../redux/actions/dialogsActions';
import {
    CLEAR_MESSAGES_STORE,
    DISPLAY_REPORT_POPUP,
    MESSAGES_INFO,
    REPLY_MESSAGE,
    SEND_MESSAGE,
    SHOW_INVITE_USERS,
    SHOW_OPTIONAL_QUESTIONS,
} from '../../redux/actions/messagesActions';

const AcceptTermsPopup = React.lazy(() => import(/* accept-terms */ '../../components/popups/AcceptTermsPopup'));
const ExperienceBasedQuestionsPopup = React.lazy(() =>
    import(/* experienced-questions */ '../../components/popups/ExperienceBasedQuestionsPopup')
);
const ChatsSidebar = React.lazy(() => import(/* chat-sidebar */ './components/ChatsSidebar'));
const Messages = React.lazy(() => import(/* messages */ './components/Messages'));
const OptionalDataPopup = React.lazy(() => import(/* optional-data-popup */ './components/popups/OptionalDataPopup'));
const MandatoryQuestionsPopup = React.lazy(() =>
    import(/* mandatory-questions */ './components/popups/MandatoryQuestionsPopup')
);
const InviteAmbassadorsModal = React.lazy(() =>
    import(/* invite-sa-popup */ './components/InviteAmbassadors/InviteAmbassadorsModal')
);
const InviteAdminsModal = React.lazy(() =>
    import(/* invite-admins-popup */ './components/InviteAdmins/InviteAdminsModal')
);
const NotificationMethodPopup = React.lazy(() =>
    import(/* notification-popup */ './components/popups/NotificationMethodPopup')
);
const ChatMembersPopup = React.lazy(() => import(/* chat-members-popup */ './components/popups/ChatMembersPopup'));
const ProspectInfoPopup = React.lazy(() => import(/* prospect-info-popup */ './components/popups/ProspectInfoPopup'));
const ReportMessagePopup = React.lazy(() =>
    import(/* report-message-popup */ './components/popups/ReportMessagePopup')
);
const MobileAppPromotionPopup = React.lazy(() =>
    import(/* experienced-questions */ '../../components/popups/MobileAppPromotionPopup')
);
const RegisterComponent = React.lazy(() => import(/* registration-start */ '../../components/register'));
const LoginComponent = React.lazy(() => import(/* login-start */ '../../components/auth'));

let questionsInProcess = false;

const Messaging = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const params = useParams();

    const account = useSelector((state) => state.account);
    const { groupChats } = useSelector((state) => state.main);
    const { institution, isMobile } = useSelector((state) => state.widget);
    const notificationMethod = useSelector((state) => state.notificationMethodPopup);
    const currentChat = useSelector((state) => state.currentChat);
    const experienceBasedQuestions = useSelector((state) => state.experienceBasedQuestionsPopup);
    const messages = useSelector((state) => state.messages);
    const { firstMessage, isOpened } = useSelector((state) => state.registrationPopup);
    const loginPopup = useSelector((state) => state.loginPopup);
    const dialogs = useSelector((state) => state.dialogs);
    const acceptTermsPopupIsOpened = useSelector((state) => state.acceptTermsPopup.isOpened);
    const reportMessage = useSelector((state) => state.reportMessage);

    const [showMandatoryQuestions, setShowMandatoryQuestions] = useState(true);
    const [mandatoryQuestionsAnswered, setMandatoryQuestionsAnswered] = useState(false);
    const [dialogsActive, setDialogsActive] = useState(true);

    const listenerRef = React.useRef({
        dispatch,
        navigate,
        account,
        institution,
        firstMessage,
        groupChats,
        currentChat,
        params,
        messages,
    });

    useEffect(() => {
        listenerRef.current = {
            dispatch,
            navigate,
            account,
            institution,
            firstMessage,
            groupChats,
            currentChat,
            params,
            messages,
        };
    }, [dispatch, navigate, account, institution, firstMessage, groupChats, currentChat, params, messages]);

    useEffect(() => {
        loadData();

        return () => {
            sessionStorageWrapper.removeItem('activePage');

            dispatch({
                type: CLEAR_MESSAGES_STORE,
            });
            dispatch({
                type: CLEAR_DIALOGS_STORE,
            });
            dispatch({
                type: CHANGE_CHATS_TAB,
                payload: {
                    chats_type: 1,
                    removeFirstChat: true,
                    removeLastActiveChats: true,
                    isFirstLoading: true,
                },
            });
            dispatch({
                type: REPLY_MESSAGE,
                payload: {
                    activeMessage: null,
                    activePublisher: null,
                },
            });
            dispatch({
                type: SHOW_OPTIONAL_QUESTIONS,
                payload: { showOptionalQuestions: true },
            });
            dispatch({
                type: ACCOUNT.RESET_QUESTIONS,
            });
            dispatch({
                type: SET_ACCEPTED,
                payload: { accepted: true },
            });
            sessionStorageWrapper.removeItem('navigateOutOfDialogPage');
        };
    }, []);

    useEffect(() => {
        if (firstMessage && account.token) {
            sendFirstMessage(firstMessage, params.dialog_id);
        }
        // for case when user on dialogs and clicking on some chat in chatbox
        if (window.location.href.includes('?direct=true')) {
            loadData();
        }
    }, [params.dialog_id]);

    const showAcceptTerms = useCallback(
        (isOnSendMessage = false) => {
            const { account_info: accountInfo } = account;
            if (
                accountInfo &&
                accountInfo.key === 'prospect' &&
                (!accountInfo.mailing ||
                    !currentChat.university ||
                    !accountInfo.mailing.find((item) => item.id === currentChat.university.id))
            ) {
                dispatch({
                    type: ACCOUNT.OPEN_ACCEPT_TERMS_POPUP,
                    payload: {
                        university: currentChat.university ? currentChat.university : null,
                        chatName: currentChat.chatsType === 4 && currentChat.data ? currentChat.data.name : null,
                        ambassadorName:
                            currentChat.data && currentChat.data.members && currentChat.data.members[0]
                                ? currentChat.data.members[0].name
                                : '',
                        saveAcceptedTerms: true,
                        isOnSendMessage,
                    },
                });
            }
        },
        [dispatch, account, currentChat]
    );

    const sendFirstMessage = useCallback(
        (firstMessage, dialogId) => {
            dispatch({
                type: REMOVE_FIRST_MESSAGE,
            });
            const onSuccess = () => {
                dispatch({
                    type: CHAT_INFO,
                    payload: {
                        dialog_id: dialogId,
                    },
                });

                if (!account.dialogIds.includes(+dialogId)) {
                    dispatch({
                        type: ADD_NEW_DIALOG_TO_IDS,
                        payload: {
                            id: dialogId,
                        },
                    });
                    bindDialog(dialogId);
                }
            };
            dispatch({
                type: SEND_MESSAGE,
                payload: {
                    type: 'text',
                    text: firstMessage,
                    dialog_id: dialogId,
                },
                onSuccess,
            });

            if (!account.notificationData) {
                dispatch({
                    type: OPEN_NOTIFICATION_METHOD_POPUP,
                });
            }
        },
        [dispatch, account]
    );

    const loadData = useCallback(() => {
        const { dispatch, navigate, account, institution, firstMessage, groupChats } = listenerRef.current;
        const { dialog_id: dialogId } = params;

        const institutionSlug = institution ? institution.slug : null;

        dispatch({
            type: CLEAR_MESSAGES_STORE,
        });
        dispatch({
            type: CLEAR_DIALOGS_STORE,
        });

        questionsInProcess = false;
        const isPopcardLead = window.location?.search?.includes('popcardLead');

        if (dialogId) {
            if (!isPopcardLead) {
                sessionStorageWrapper.setItem(
                    'activePage',
                    JSON.stringify({
                        id: dialogId,
                        universityId: getUniversityId(),
                        isGroup: isUniGroup(),
                        page: 'chat',
                        institutionSlug,
                    })
                );
            }
            const privateCode = window.location.href.split('dialog_code=')[1]
                ? window.location.href.split('dialog_code=')[1].split('&')[0]
                : null;

            const onSuccess = (chat, privateCode) => {
                const type = chat.type;
                if (type !== 4 && (!account.account_info || !account.account_info.id)) {
                    navigate(`${getCommonRoutePath()}/interaction/sign-in`);
                    return;
                }
                if (type === 4) {
                    dispatch({
                        type: GROUP_DIALOGS_INFO,
                        payload: {
                            isFirstLoad: true,
                            resetMessagesDialogId: chat.id,
                        },
                    });

                    watchChat(dialogId, chat.university_id);
                    if (!account.dialogIds || (account.dialogIds && !account.dialogIds.includes(+dialogId))) {
                        bindDialog(dialogId);
                    }
                    if (window.location.href.includes('?signUp=true') && !account.token) {
                        trackGetStarted(chat.university_id, 'button', true);
                        dispatch(
                            startRegistration({
                                university: { id: chat.university_id },
                                id: chat.id,
                                name: chat.name,
                                type: 'group-chat',
                            })
                        );
                    }
                } else if (account.account_info) {
                    dispatch({
                        type: account.account_info.key === 'prospect' ? PROSPECT_DIALOGS_INFO : AMBASSADOR_DIALOGS_INFO,
                        payload: {
                            isFirstLoad: true,
                            resetMessagesDialogId: chat.id,
                        },
                    });
                }
                dispatch({
                    type: MESSAGES_INFO,
                    payload: {
                        dialog_id: dialogId,
                        dialog_type: type,
                        noResetForNewMessages: true,
                        privateCode: privateCode,
                    },
                });

                setDialogsActive(false);

                if (firstMessage && account.token) {
                    sendFirstMessage(firstMessage, dialogId);
                }
                loadOptionalQuestions(chat?.university?.id);
            };
            const onFailure = (closed) => {
                if (!account.account_info) {
                    navigate(`${getCommonRoutePath()}/interaction/sign-in`);
                } else {
                    navigate(`${getCommonRoutePath()}/interaction/dialogs`);
                    dispatch({
                        type: CHANGE_CHATS_TAB,
                        payload: {
                            chats_type: 1,
                            removeTempActiveChat: true,
                        },
                    });
                    dispatch({
                        type: account.account_info.key === 'prospect' ? PROSPECT_DIALOGS_INFO : AMBASSADOR_DIALOGS_INFO,
                        payload: {
                            isFirstLoad: true,
                        },
                    });
                    if (closed && account.account_info.key === 'prospect') {
                        const isAnyActiveGroup = groupChats && groupChats.items && groupChats.items.length;

                        const goTo = (chats) => {
                            dispatch({
                                type: CLOSE_ACTION_MODAL,
                            });
                            navigate(
                                getCommonRoutePath() +
                                    (chats ? (isAnyActiveGroup ? '/group-chats' : '/chat-to-us') : '')
                            );
                        };
                        dispatch({
                            type: OPEN_ACTION_MODAL,
                            payload: {
                                children: (
                                    <div className="general-popup">
                                        <h2 className="modal-title">Group Chat is inactive</h2>
                                        <p className="not-active-chat-text">
                                            Sorry, this Group Chat is no longer active. Please get in touch with our
                                            friendly ambassadors directly.
                                        </p>
                                        <div className="btn-wrap confirm-ban-buttons">
                                            <Button
                                                type="submit"
                                                className="el-btn general-action-button"
                                                onClick={() => goTo(true)}
                                            >
                                                {isAnyActiveGroup ? 'All group chats' : 'See all Ambassadors'}
                                            </Button>
                                            <Button
                                                type="submit"
                                                className="el-btn general-action-button cancel"
                                                onClick={() => goTo(false)}
                                            >
                                                Go to homepage
                                            </Button>
                                        </div>
                                    </div>
                                ),
                                class: 'inactive-chat-popup',
                            },
                        });
                    }
                }
            };

            dispatch({
                type: CHAT_INFO,
                payload: {
                    dialog_id: dialogId,
                    firstLoad: true,
                    privateCode: privateCode,
                },
                onSuccess,
                onFailure,
            });
        } else {
            const navigateOutOfDialogPage = JSON.parse(sessionStorageWrapper.getItem('navigateOutOfDialogPage'));
            if (navigateOutOfDialogPage) {
                setDialogsActive(false);
            }
            sessionStorageWrapper.removeItem('activePage');
            dispatch({
                type: RESET_ACTIVE_CHAT,
                payload: {},
            });

            if (account.account_info) {
                dispatch({
                    type: account.account_info.key === 'prospect' ? PROSPECT_DIALOGS_INFO : AMBASSADOR_DIALOGS_INFO,
                    payload: {
                        isFirstLoad: true,
                    },
                });
            } else {
                dispatch({
                    type: GROUP_DIALOGS_INFO,
                    payload: {
                        isFirstLoad: true,
                    },
                });
            }
            showAcceptTerms();
        }
    }, []);

    const loadOptionalQuestions = useCallback((universityId) => {
        const { params, currentChat, messages } = listenerRef.current;
        const { dialog_id: dialogId } = params;
        if (
            account.account_info &&
            account.account_info.key === 'prospect' &&
            !currentChat.hasAccepted &&
            !currentChat.universityQuestionsAnswered &&
            currentChat.university &&
            ((messages &&
                messages.items &&
                messages.items.length > 1 &&
                currentChat.chatsType === 1 &&
                checkIfChatAnswered(messages)) ||
                currentChat.chatsType === 4)
        ) {
            dispatch({
                type: UNIVERSITY_QUESTIONS,
                payload: {
                    universityId,
                    engagementId: dialogId,
                    engagementType: currentChat.chatsType === 4 ? 'group-chat' : 'chat',
                },
                meta: { onSuccess: showAcceptTerms },
            });
        }
    }, []);

    const loadMandatoryQuestions = useCallback(() => {
        dispatch({
            type: MANDATORY_QUESTIONS,
        });
    }, [dispatch]);

    const handleSubmitOptionalQuestions = useCallback(
        (answers) => {
            const { account, currentChat } = listenerRef.current;
            const universityId = currentChat.university ? currentChat.university.id : null;

            const onSuccess = () => {
                let utmData = {};
                const uniOrGroupId = getUniversityId();

                if (localStorageWrapper.getItem('utmData_' + uniOrGroupId)) {
                    const dataTemp = JSON.parse(localStorageWrapper.getItem('utmData_' + uniOrGroupId));
                    utmData = {
                        source: dataTemp.utm_source,
                        medium: dataTemp.utm_medium,
                        campaign: dataTemp.utm_campaign,
                        user_came_from: dataTemp.cameFrom,
                    };
                }

                dispatch({
                    type: SHOW_OPTIONAL_QUESTIONS,
                    payload: { showOptionalQuestions: false, universityQuestionsAnswered: true },
                });

                // TODO: with new flow we are saving it on registration and accept terms steps, we don't need it anymore I believe,
                //  only for old users and could be removed in near future, or we could just save it with SAVE_UNIVERSITY_QUESTIONS (+server side changes)
                dispatch({
                    type: SAVE_UNIVERSITY_USER_UTM_DATA,
                    payload: {
                        universityId,
                        utmData,
                    },
                });
            };

            const onError = () => {
                questionsInProcess = false;
            };

            if (!questionsInProcess) {
                questionsInProcess = true;
                dispatch({
                    type: SAVE_UNIVERSITY_QUESTIONS,
                    payload: {
                        universityId,
                        answers,
                        onlyMandatory: account.onlyMandatory,
                        engagementId: currentChat.activeChat,
                        engagementType: currentChat.chatsType === 4 ? 'group-chat' : 'chat',
                    },
                    onSuccess: onSuccess,
                    onError: onError,
                });
            }
        },
        [account, currentChat]
    );

    const handleSubmitMandatoryQuestions = useCallback((answers) => {
        dispatch({
            type: SAVE_MANDATORY_QUESTIONS,
            payload: {
                answers,
            },
        });

        setShowMandatoryQuestions(false);
        setMandatoryQuestionsAnswered(true);
    }, []);

    const openMandatoryQuestionsPopup = useCallback(() => {
        setShowMandatoryQuestions(true);
    }, []);

    const handleCloseOptionalQuestions = useCallback(() => {
        const isSomeQuestionsMandatory = account.questions.some((item) => item.isMandatory);
        dispatch({
            type: SHOW_OPTIONAL_QUESTIONS,
            payload: { showOptionalQuestions: false },
        });
        if (!isSomeQuestionsMandatory) {
            handleSubmitOptionalQuestions([]);
        }
    }, [dispatch, account]);

    const handleCloseInviteUsersPopup = useCallback(() => {
        dispatch({
            type: SHOW_INVITE_USERS,
            payload: { inviteUsersIsOpened: false },
        });
    }, [dispatch]);

    const handleCloseMandatoryQuestions = useCallback(() => {
        setShowMandatoryQuestions(false);
    }, []);

    const handleCloseChatMembersPopup = useCallback(() => {
        dispatch({
            type: SHOW_CHAT_MEMBERS,
            payload: {
                showMembers: false,
            },
        });
    }, [dispatch]);

    const handleCloseProspectInfoPopup = useCallback(() => {
        dispatch({
            type: DISPLAY_PROSPECT_INFO,
            payload: false,
        });
    }, [dispatch]);

    const handleCloseReportMessagePopup = useCallback(() => {
        dispatch({
            type: DISPLAY_REPORT_POPUP,
            payload: false,
        });
    }, [dispatch]);

    const handleGoBack = useCallback(() => {
        if (dialogsActive) {
            if (sessionStorageWrapper.getItem('goBack')) {
                sessionStorageWrapper.removeItem('goBack');
            } else {
                navigate(getCommonRoutePath());
            }
        } else {
            setDialogsActive(true);
        }
    }, [dialogsActive, navigate]);

    const goToMessages = useCallback(() => {
        setDialogsActive(false);
    }, []);

    const handleCloseRegisterPopup = useCallback(() => {
        dispatch({
            type: CLOSE_REGISTRATION_POPUP,
        });
    }, [dispatch]);

    const handleCloseLoginPopup = useCallback(() => {
        dispatch({
            type: CLOSE_LOGIN_POPUP,
        });
    }, [dispatch]);

    const handleAskExperienceBasedQuestion = useCallback(
        ({ questionToAsk }) => {
            sendFirstMessage(questionToAsk, params.dialog_id);
        },
        [params]
    );

    const { universitySettings } = experienceBasedQuestions;

    const isAllowToSkipBasedQuestions = institution
        ? !institution?.isAllowedToSkipExperienceBasedQuestions
        : !universitySettings?.isAllowedToSkipExperienceBasedQuestions;
    const isIframeless = isIframelessVersion();

    const members = [];
    if (
        currentChat.data &&
        currentChat.data.members &&
        account.account_info &&
        account.account_info.key === 'univercity-student'
    ) {
        // we don't need current user
        for (const member of currentChat.data.members) {
            if (member.id !== account.account_info.id) {
                members.push(member);
            }
        }
    }

    if (messages.isLoading || dialogs.isLoading) {
        return (
            <div className="chat chat-loader">
                <div className="chat-block">
                    <Loader />
                </div>
            </div>
        );
    }

    let showMandatoryAndOptional =
        currentChat.chatsType === 4 &&
        account.account_info &&
        account.account_info.mailing &&
        currentChat.university &&
        account.account_info.mailing.find((item) => item.id === currentChat.university.id);
    if (messages && messages.items && messages.items.length > 1 && currentChat.chatsType === 1) {
        showMandatoryAndOptional = checkIfChatAnswered(messages);
    }
    return (
        <div
            id="page-body"
            className={cn('interaction-page page-body messaging js-index', {
                iframless: isIframeless,
                'in-iframe': isInIframe,
                mobile: isMobile,
            })}
        >
            <Suspense fallback={null}>
                <Messages
                    mandatoryQuestionsAnswered={mandatoryQuestionsAnswered}
                    openMandatoryQuestionsPopup={openMandatoryQuestionsPopup}
                    isMobile={isMobile}
                    handleGoBackMobile={handleGoBack}
                    showAcceptTerms={showAcceptTerms}
                    hideMessagesBlockMobile={isMobile && dialogsActive}
                />
            </Suspense>
            {(!isMobile || dialogsActive) && <ChatsSidebar isMobile={isMobile} goToMessages={goToMessages} />}
            {account && account.account_info && account.account_info.key === 'univercity-student' && (
                <>
                    <Modal
                        open={currentChat.inviteUsersIsOpened && currentChat.inviteUsersType === 'ambassadors'}
                        styles={{ body: { padding: 0 } }}
                        centered={true}
                        footer={null}
                        closable={false}
                        maskClosable={true}
                        onCancel={handleCloseInviteUsersPopup}
                        destroyOnClose
                        className="invite-sa-modal"
                    >
                        <Suspense fallback={null}>
                            <InviteAmbassadorsModal
                                account={account}
                                dialog={currentChat.data}
                                members={members}
                                onClosePopup={handleCloseInviteUsersPopup}
                            />
                        </Suspense>
                    </Modal>

                    <Modal
                        open={currentChat.inviteUsersIsOpened && currentChat.inviteUsersType === 'admins'}
                        styles={{ body: { padding: 0 } }}
                        centered={true}
                        footer={null}
                        closable={false}
                        maskClosable={true}
                        onClosed={handleCloseInviteUsersPopup}
                        destroyOnClose
                        className="invite-sa-modal"
                    >
                        <Suspense fallback={null}>
                            <InviteAdminsModal
                                account={account}
                                dialog={currentChat.data}
                                members={members}
                                onClosePopup={handleCloseInviteUsersPopup}
                            />
                        </Suspense>
                    </Modal>
                </>
            )}
            {account.account_info && account.account_info.key === 'prospect' && (
                <>
                    {showMandatoryAndOptional &&
                    account.hasMandatoryQuestions &&
                    !mandatoryQuestionsAnswered &&
                    !account.account_info.isAnsweredMandatoryQuestion ? (
                        <Modal
                            open={showMandatoryQuestions}
                            centered={true}
                            footer={null}
                            closable={false}
                            onCancel={handleCloseMandatoryQuestions}
                            width={500}
                            className="optional-questions-popup"
                        >
                            <Suspense fallback={null}>
                                <MandatoryQuestionsPopup
                                    ambassador={currentChat.ambassador}
                                    loadData={loadMandatoryQuestions}
                                    questions={account.mandatoryQuestions}
                                    onSubmit={handleSubmitMandatoryQuestions}
                                    onClose={handleCloseMandatoryQuestions}
                                />
                            </Suspense>
                        </Modal>
                    ) : (
                        showMandatoryAndOptional &&
                        currentChat.university &&
                        !currentChat.hasAccepted && (
                            <Suspense fallback={null}>
                                <OptionalDataPopup
                                    accountInfo={account.account_info}
                                    loadedQuestionsUniversityId={account.loadedQuestionsUniversityId}
                                    currentChat={currentChat}
                                    loadOptionalQuestions={loadOptionalQuestions}
                                    questions={account.questions}
                                    onSubmit={handleSubmitOptionalQuestions}
                                    handleCloseOptionalQuestions={handleCloseOptionalQuestions}
                                />
                            </Suspense>
                        )
                    )}

                    <Modal
                        open={notificationMethod.isOpened}
                        centered={true}
                        footer={null}
                        closable={false}
                        width={430}
                        className="notification-popup"
                        maskClosable={false}
                    >
                        {notificationMethod.isOpened ? (
                            <FocusTrap>
                                <Suspense fallback={null}>
                                    <NotificationMethodPopup
                                        account={account}
                                        universityId={currentChat.university ? currentChat.university.id : null}
                                        prospectEmail={account.account_info.email}
                                    />
                                </Suspense>
                            </FocusTrap>
                        ) : (
                            false
                        )}
                    </Modal>
                </>
            )}
            {(!account.account_info || !account.account_info.key) && (
                <>
                    <Modal
                        open={isOpened}
                        styles={{ body: { padding: 0 } }}
                        width={470}
                        centered={true}
                        footer={null}
                        closable={false}
                        maskClosable={true}
                        onCancel={handleCloseRegisterPopup}
                        className="register-modal"
                    >
                        <Suspense fallback={null}>
                            <RegisterComponent
                                id={currentChat?.data?.id}
                                type={currentChat.chatsType === 4 ? 'group-chat' : 'chat'}
                                groupChatAction={true}
                                onGroupChat={currentChat.chatsType === 4}
                                university={currentChat.university}
                                name={currentChat.name}
                                handleClosePopup={handleCloseRegisterPopup}
                            />
                        </Suspense>
                    </Modal>

                    <Modal
                        open={loginPopup.isOpened}
                        styles={{ body: { padding: 0 } }}
                        width={470}
                        centered={true}
                        footer={null}
                        closable={false}
                        maskClosable={true}
                        onCancel={handleCloseLoginPopup}
                        className="register-modal"
                    >
                        <Suspense fallback={null}>
                            <LoginComponent
                                id={currentChat?.data?.id}
                                type={currentChat.chatsType === 4 ? 'group-chat' : 'chat'}
                                groupChatAction={true}
                                onGroupChat={currentChat.chatsType === 4}
                                university={currentChat.university}
                                name={currentChat.name}
                                handleClosePopup={handleCloseLoginPopup}
                            />
                        </Suspense>
                    </Modal>
                </>
            )}
            <Modal
                open={currentChat.showMembers}
                centered={true}
                footer={null}
                closable={false}
                onCancel={handleCloseChatMembersPopup}
                width={500}
            >
                <Suspense fallback={null}>
                    <ChatMembersPopup onClose={handleCloseChatMembersPopup} />
                </Suspense>
            </Modal>
            <Modal
                open={currentChat.showProspectInfo}
                centered={true}
                footer={null}
                closable={false}
                onCancel={handleCloseProspectInfoPopup}
                width={500}
                zIndex={1100}
            >
                <Suspense fallback={null}>
                    <ProspectInfoPopup onClose={handleCloseProspectInfoPopup} />
                </Suspense>
            </Modal>
            <Modal
                open={reportMessage.showReportMessagePopup}
                centered={true}
                footer={null}
                closable={false}
                onCancel={handleCloseReportMessagePopup}
                width={520}
                zIndex={1100}
                className="confirm-modal"
            >
                <Suspense fallback={null}>
                    <ReportMessagePopup onClose={handleCloseReportMessagePopup} />
                </Suspense>
            </Modal>
            {acceptTermsPopupIsOpened && (
                <Suspense fallback={null}>
                    <AcceptTermsPopup
                        engagementId={currentChat.activeChat}
                        engagementType={currentChat.chatsType === 4 ? 'group-chat' : 'chat'}
                    />
                </Suspense>
            )}
            <Suspense fallback={null}>
                <ExperienceBasedQuestionsPopup
                    handleAskExperienceBasedQuestion={handleAskExperienceBasedQuestion}
                    maskClosable={isAllowToSkipBasedQuestions}
                />
            </Suspense>
            <Suspense fallback={null}>
                <MobileAppPromotionPopup />
            </Suspense>
        </div>
    );
};

export default Messaging;
